// Browser Position
export const isScrolledIntoView = (elem: HTMLElement): boolean => {
    const docViewTop = window.scrollY;
    const docViewBottom = docViewTop + window.innerHeight;
    const elemTop = elem.getBoundingClientRect().top + window.scrollY;

    return elemTop <= docViewBottom;
};

export const scrollToElement = (target: HTMLElement): void => {
    requestAnimationFrame(() => {
        target.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
        });
        target.focus({ preventScroll: true });
    });
};

// Fade In Animation
export const fadeInBlock = (): void => {
    const blocks = document.querySelectorAll<HTMLElement>(".fade-block");
    blocks.forEach(block => {
        if (isScrolledIntoView(block)) {
            block.classList.add("visible");
        }
    });
    setScrollEvents();
};

const setScrollEvents = () => {
    window.addEventListener('scroll', scrollEvents, { passive: true });
    window.addEventListener('resize', scrollEvents, { passive: true });
};

const scrollEvents = () => {   
  let scrolling = false; 
    if (!scrolling) {
        scrolling = true;
        requestAnimationFrame(() => {
            scrolling = false;
            fadeInBlock();
        });
    }
}

// Targeting by element or selector
export const normalizeTargets = (
  target: string | HTMLElement | NodeListOf<HTMLElement> | HTMLElement[],
  filterTypes?: Array<new (...args: any[]) => HTMLElement>
): HTMLElement[] => {
  let elements: HTMLElement[] = [];

  if (typeof target === 'string') {
    elements = Array.from(document.querySelectorAll<HTMLElement>(target));
  } else if (target instanceof HTMLElement) {
    elements = [target];
  } else if (target instanceof NodeList) {
    elements = Array.from(target as NodeListOf<HTMLElement>);
  } else if (Array.isArray(target)) {
    elements = target;
  }
  
  // Apply filter if provided
  if (filterTypes && filterTypes.length > 0) {
    return elements.filter(el => 
      filterTypes.some(type => el instanceof type)
    );
  }
  
  return elements;
};

// Visibility toggles: [data-visibility-toggle] triggers with aria-controls target and optional data-label-show/data-label-hide
export const setVisibilityToggles = (
  target: string | HTMLElement | NodeListOf<HTMLElement> | HTMLElement[] = '[data-visibility-toggle]'
): void => {
  normalizeTargets(target).forEach(trigger => {
    if (trigger.dataset.visibilityToggleBound) return;

    const controlsId = trigger.getAttribute('aria-controls');
    const panel = controlsId ? document.getElementById(controlsId) : null;
    if (!panel) return;

    const labelElement = trigger.querySelector<HTMLElement>('.btn-label') ?? trigger;
    const showLabel = trigger.dataset.labelShow;
    const hideLabel = trigger.dataset.labelHide;

    const applyState = (isVisible: boolean) => {
      panel.hidden = !isVisible;
      trigger.setAttribute('aria-expanded', String(isVisible));

      const label = isVisible ? hideLabel : showLabel;
      if (label) labelElement.textContent = label;
    };

    trigger.dataset.visibilityToggleBound = 'true';
    applyState(!panel.hidden);

    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      applyState(Boolean(panel.hidden));
    });
  });
};

// Storage
export const storage = {
  get(key: string): string | null {
    try { return localStorage.getItem(key); } catch { return null; }
  },
  set(key: string, value: string): boolean {
    try { localStorage.setItem(key, value); return true; } catch { return false; }
  },
  remove(key: string): void {
    try { localStorage.removeItem(key); } catch { /* no-op */ }
  }
};

// Cookie helpers (use only if server needs it)
export const cookies = {
  set(name: string, value: string, days = 365, path = '/'): void {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=${path}`;
  },
  get(name: string): string | null {
    const m = document.cookie.match(new RegExp(`(?:^|; )${encodeURIComponent(name)}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
  },
  remove(name: string, path = '/'): void {
    document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
  }
};

export class Backdrop {
    private element: HTMLDivElement | null = null;
    private onClick?: () => void;

    constructor(onClick?: () => void) {
        this.onClick = onClick;
    }

    show(): void {
        if (this.element) return;
        
        this.element = document.createElement('div');
        this.element.className = 'nav-backdrop';
        this.element.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 9;
            opacity: 0;
            transition: opacity 0.3s ease;
        `;
        
        document.body.appendChild(this.element);
        
        // Trigger fade-in
        setTimeout(() => {
            if (this.element) this.element.style.opacity = '1';
        }, 10);
        
        if (this.onClick) {
            this.element.addEventListener('click', this.onClick);
        }
    }

    hide(): void {
        if (!this.element) return;
        
        this.element.style.opacity = '0';
        
        setTimeout(() => {
            this.element?.remove();
            this.element = null;
        }, 300);
    }
}

export type DebouncedFunction<T extends unknown[]> = ((...args: T) => void) & {
    cancel: () => void;
    flush: () => void;
    pending: () => boolean;
};

export const debounce = <T extends unknown[]>(
    func: (...args: T) => void,
    timeout: number
): DebouncedFunction<T> => {
    let timer: number | undefined;
    let lastArgs: T | undefined;

    const debounced = (...args: T) => {
        lastArgs = args;

        if (timer !== undefined) {
            window.clearTimeout(timer);
        }

        timer = window.setTimeout(() => {
            timer = undefined;
            if (lastArgs) {
                func(...lastArgs);
            }
            lastArgs = undefined;
        }, timeout);
    };

    debounced.cancel = () => {
        if (timer !== undefined) {
            window.clearTimeout(timer);
            timer = undefined;
        }
        lastArgs = undefined;
    };

    debounced.flush = () => {
        if (timer === undefined || !lastArgs) {
            return;
        }

        window.clearTimeout(timer);
        timer = undefined;
        const args = lastArgs;
        lastArgs = undefined;
        func(...args);
    };

    debounced.pending = () => timer !== undefined;

    return debounced;
};